/** Columns of the catalogue rows grid (plan §3.9): include, product, kind, grain, joins (when the
 *  registry has `tour.join`: joins, joined by, a pending split-off), languages, start times,
 *  package, last sync, itinerary / AI (the mode, AI-built outputs, the planner's latest run and
 *  accepted-plan flags), coupling, pending change. Inline edits write one product's settings
 *  through `onSet` (the PATCH answers with the registry's reason when it refuses); coupling
 *  toggles open the reason dialog. */
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useState, type ComponentProps } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type {
  CatalogueCoverageWarning,
  CatalogueOutput,
  CatalogueRow,
  CatalogueScope,
  SettingSpec,
} from '@/api/catalogue';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { AiStatusChips } from './AiStatus';
import {
  aiRunLabel,
  choicesFor,
  compactList,
  entityLabel,
  isIncluded,
  joinerLabel,
  joinTargetOf,
  KEY,
  optionLanguages,
  optionStartTimes,
  packageNotes,
  pendingTone,
  producedOutputs,
  settingOf,
  sourceLabel,
  STICKY_HINT,
  syncStatusTone,
  valueLabel,
} from './catalogueModel';
import { pendingSplitOffSentence, splitOffFromLabel, TourLink } from './JoinNotes';
import { useCatalogueProductRef } from './queries';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const capitalize = (v: string) => (v ? v[0]!.toUpperCase() + v.slice(1) : v);

export interface CatalogueColumnDeps {
  specs: Map<string, SettingSpec>;
  /** Write product-scope settings (`null` resets). */
  onSet: (row: CatalogueRow, settings: Record<string, unknown>) => void;
  onOpen: (row: CatalogueRow) => void;
  onCoupling: (row: CatalogueRow, output: CatalogueOutput) => void;
  /** Rows with a write in flight (their controls are disabled). */
  busy: ReadonlySet<number>;
  coverageById: ReadonlyMap<number, CatalogueCoverageWarning>;
  /** The page's catalogue (a joined product off this page is looked up there). */
  scope: CatalogueScope;
  /** This page's rows by vendor id (names a row's joiners when they are on the page). */
  rowsByVendor: ReadonlyMap<string, CatalogueRow>;
}

const rowName = (r: Pick<CatalogueRow, 'title' | 'name'>) => r.title || r.name;

function SmallChip(props: ComponentProps<typeof Chip>) {
  return <Chip size="small" {...props} sx={{ height: 20, fontSize: 11, ...props.sx }} />;
}

function KindCell({ row, deps }: { row: CatalogueRow; deps: CatalogueColumnDeps }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const spec = deps.specs.get(KEY.kind);
  const kind = row.kind;
  if (!spec) return <span>{String(kind?.value ?? '—')}</span>;
  const label = valueLabel(spec, kind?.value);
  const ops = kind?.source === 'ops';
  return (
    <>
      <Tooltip
        describeChild
        title={`${sourceLabel(kind?.source)}${row.provisional ? ' · provisional: triaged without the package detail' : ''} — click to correct`}
      >
        <Chip
          size="small"
          label={row.provisional ? `${label}?` : label}
          variant={ops ? 'filled' : 'outlined'}
          color={ops ? 'primary' : 'default'}
          onClick={(e) => setAnchor(e.currentTarget)}
          disabled={deps.busy.has(row.id)}
          aria-label={`Kind of ${row.title || row.name}: ${label}`}
          sx={{ maxWidth: '100%', height: 22, fontSize: 12 }}
        />
      </Tooltip>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {choicesFor(spec).map((c) => (
          <MenuItem
            key={c.value}
            selected={c.value === kind?.value}
            onClick={() => {
              setAnchor(null);
              if (c.value !== kind?.value) deps.onSet(row, { [KEY.kind]: c.value });
            }}
          >
            {c.label}
          </MenuItem>
        ))}
        {ops && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              deps.onSet(row, { [KEY.kind]: null });
            }}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          >
            <ListItemText primary="Reset to triage's kind" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

function GrainCell({ row, deps }: { row: CatalogueRow; deps: CatalogueColumnDeps }) {
  const spec = deps.specs.get(KEY.grain);
  const grain = settingOf(row, KEY.grain);
  if (!spec || !grain) return <Typography variant="body2" color="text.secondary">—</Typography>;
  // A product that joins another builds no tour, and any other grain is refused: no selector.
  if (joinTargetOf(row)) {
    return (
      <Tooltip describeChild title="It joins another product's tour (Joins), so it builds no tour of its own.">
        <Typography variant="body2">{valueLabel(spec, grain.value)}</Typography>
      </Tooltip>
    );
  }
  const choices = choicesFor(spec, row.options);
  // Offered only when a tour per option is possible (options other than DEFAULT).
  if (choices.every((c) => c.value !== 'option' || c.blocked)) {
    return (
      <Tooltip describeChild title={`${sourceLabel(grain.source)} · one tour per option needs options other than DEFAULT`}>
        <Typography variant="body2">{valueLabel(spec, grain.value)}</Typography>
      </Tooltip>
    );
  }
  return (
    <TextField
      select
      size="small"
      variant="standard"
      value={String(grain.value ?? '')}
      disabled={deps.busy.has(row.id)}
      onChange={(e) => deps.onSet(row, { [KEY.grain]: e.target.value })}
      inputProps={{ 'aria-label': `Tours of ${row.title || row.name}` }}
      SelectProps={{ renderValue: (v) => valueLabel(spec, v) }}
      InputProps={{ disableUnderline: true, sx: { fontSize: 13 } }}
      sx={{ minWidth: 0, width: '100%' }}
    >
      {choices.map((c) => (
        <MenuItem key={c.value} value={c.value} disabled={c.blocked !== null}>
          {c.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** `tour.join` (plan 2026-10-11 product joins §9): a product that joins another's tour reads
 *  `joins ‹A›` (a click opens A); a product others join reads `+N joined` from its row's
 *  `joined_by`, the tooltip naming them (by name when they are on this page); a cleared join
 *  waiting for the product's next sync reads "splits off at next sync" with the tour it leaves
 *  (`pending_split_off`; a click opens the product, whose drawer says the rest). */
function JoinsCell({ row, deps }: { row: CatalogueRow; deps: CatalogueColumnDeps }) {
  const target = joinTargetOf(row);
  const onPage = target ? deps.rowsByVendor.get(target) : undefined;
  // Off this page, the joined product is looked up by its vendor id (cached per product).
  const lookup = useCatalogueProductRef(target && !onPage ? deps.scope : null, target);
  const targetRow = onPage ?? lookup.data ?? undefined;
  const joiners = row.joined_by;
  // Joining again cancels a split-off (the server drops it): a join on the row wins.
  const splitOff = target ? null : row.pending_split_off;
  if (target) {
    const name = targetRow ? rowName(targetRow) : target;
    return (
      <Tooltip
        describeChild
        title={`Builds no tour of its own: its product and option IDs are additional IDs of the tour of ${name} (${target}).`}
      >
        {/* A plain Chip: the tooltip needs a child that holds a ref. */}
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={`joins ${name}`}
          {...(targetRow ? { onClick: () => deps.onOpen(targetRow) } : {})}
          aria-label={`${rowName(row)} joins the tour of ${name}`}
          sx={{ maxWidth: '100%', height: 20, fontSize: 11 }}
        />
      </Tooltip>
    );
  }
  if (!joiners.length && !splitOff) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack spacing={0.25} alignItems="flex-start" sx={{ py: 0.5, minWidth: 0 }}>
      {splitOff && (
        <>
          <Tooltip describeChild title={pendingSplitOffSentence(splitOff)}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="splits off at next sync"
              onClick={() => deps.onOpen(row)}
              aria-label={`${rowName(row)} splits off ${splitOffFromLabel(splitOff)} at its next sync`}
              sx={{ maxWidth: '100%', height: 20, fontSize: 11 }}
            />
          </Tooltip>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>
            from <TourLink id={splitOff.from_tour_id} name={splitOff.from_tour_name} />
          </Typography>
        </>
      )}
      {joiners.length > 0 && (
        <Tooltip
          describeChild
          title={`Joined into its tour as additional IDs: ${joiners.map((v) => joinerLabel(v, deps.rowsByVendor.get(v))).join(', ')}`}
        >
          <Chip
            size="small"
            color="primary"
            label={`+${joiners.length} joined`}
            aria-label={`${joiners.length} joined into the tour of ${rowName(row)}`}
            sx={{ height: 20, fontSize: 11 }}
          />
        </Tooltip>
      )}
    </Stack>
  );
}

function PackageCell({ row, deps }: { row: CatalogueRow; deps: CatalogueColumnDeps }) {
  if (!row.is_package) return null;
  const spec = deps.specs.get(KEY.packageMode);
  const mode = settingOf(row, KEY.packageMode);
  const notes = packageNotes(String(row.kind?.value ?? ''), String(mode?.value ?? ''));
  const coverage = deps.coverageById.get(row.id);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      {spec && mode ? (
        <Tooltip describeChild title={`${valueLabel(spec, mode.value)} · ${sourceLabel(mode.source)}. ${STICKY_HINT}`}>
          <TextField
            select
            size="small"
            variant="standard"
            value={String(mode.value ?? '')}
            disabled={deps.busy.has(row.id)}
            onChange={(e) => deps.onSet(row, { [KEY.packageMode]: e.target.value })}
            inputProps={{ 'aria-label': `Package import of ${row.title || row.name}` }}
            SelectProps={{ renderValue: (v) => capitalize(String(v)) }}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13 } }}
            sx={{ minWidth: 70 }}
          >
            {choicesFor(spec).map((c) => (
              <MenuItem key={c.value} value={c.value}>
                <ListItemText primary={capitalize(c.value)} secondary={c.label} />
              </MenuItem>
            ))}
          </TextField>
        </Tooltip>
      ) : (
        <Typography variant="body2" color="text.secondary">
          package
        </Typography>
      )}
      {notes.length > 0 && (
        <Tooltip describeChild title={notes.join(' ')}>
          <SmallChip label={mode?.value === 'legs' ? 'legs' : 'note'} variant="outlined" />
        </Tooltip>
      )}
      {coverage && (
        <Tooltip
          describeChild
          title={
            coverage.status === 'unknown'
              ? 'Legs are unknown until the package detail is fetched (refresh the listing).'
              : `${coverage.legs.length} leg(s) would import nothing: not selected, or no tour.`
          }
        >
          <WarningAmberIcon fontSize="small" color="warning" aria-label="Legs coverage warning" />
        </Tooltip>
      )}
    </Stack>
  );
}

function CouplingCell({ row, deps }: { row: CatalogueRow; deps: CatalogueColumnDeps }) {
  const outputs = producedOutputs(row.outputs);
  if (!outputs.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
      {outputs.map((o) => (
        <Tooltip
          key={o.id}
          describeChild
          title={
            o.coupled
              ? `${o.ext_id} — updated by syncs. Click to decouple.`
              : `${o.ext_id} — decoupled${o.coupling_note ? `: ${o.coupling_note}` : ''}. Click to re-couple.`
          }
        >
          <Chip
            size="small"
            icon={o.coupled ? <LinkIcon /> : <LinkOffIcon />}
            label={`${entityLabel(o.entity)}${o.option_id ? ` · ${o.option_id.slice(0, 8)}` : ''}`}
            color={o.coupled ? 'default' : 'warning'}
            variant={o.coupled ? 'outlined' : 'filled'}
            onClick={() => deps.onCoupling(row, o)}
            aria-label={`${o.coupled ? 'Decouple' : 'Re-couple'} ${entityLabel(o.entity)} ${o.ext_id}`}
            sx={{ height: 22, fontSize: 11 }}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

export function buildCatalogueColumns(deps: CatalogueColumnDeps): GridColDef<CatalogueRow>[] {
  const itinerarySpec = deps.specs.get(KEY.itineraryMode);
  return [
    {
      field: 'include',
      headerName: 'Sync',
      width: 64,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const inc = settingOf(row, KEY.include);
        return (
          <Tooltip describeChild title={`${isIncluded(row) ? 'Included' : 'Not included'} · ${sourceLabel(inc?.source)}`}>
            <Checkbox
              size="small"
              checked={isIncluded(row)}
              disabled={deps.busy.has(row.id)}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => deps.onSet(row, { [KEY.include]: e.target.checked })}
              inputProps={{ 'aria-label': `Sync ${row.title || row.name}` }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Product',
      flex: 1,
      minWidth: 260,
      sortable: true,
      filterable: false,
      valueGetter: (_v, row) => row.title || row.name,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5, minWidth: 0 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => deps.onOpen(row)}
            sx={{
              p: 0,
              minWidth: 0,
              textAlign: 'left',
              justifyContent: 'flex-start',
              fontWeight: 600,
              textTransform: 'none',
              textDecoration: row.removed_ts ? 'line-through' : undefined,
            }}
          >
            {row.title || row.name}
          </Button>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ fontFamily: MONO, color: 'text.secondary' }}>
              {row.vendor_id}
            </Typography>
            {row.reference && (
              <Typography variant="caption" color="text.secondary">
                · {row.reference}
              </Typography>
            )}
            {row.removed_ts && (
              <Tooltip describeChild title={`Missing from the vendor's listing since ${formatDate(row.removed_ts)}`}>
                <SmallChip label="removed upstream" color="error" variant="outlined" />
              </Tooltip>
            )}
            {row.problem_count ? (
              <Tooltip describeChild title="Settings the resolver skipped — open the product to see why">
                <SmallChip label={`${row.problem_count} problem${row.problem_count === 1 ? '' : 's'}`} color="warning" variant="outlined" />
              </Tooltip>
            ) : null}
            {row.flags.map((f) => (
              <SmallChip key={f} label={f.replace(/_/g, ' ')} variant="outlined" />
            ))}
          </Box>
        </Box>
      ),
    },
    {
      field: 'kind',
      headerName: 'Kind',
      width: 200,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => valueLabel(deps.specs.get(KEY.kind), row.kind?.value),
      renderCell: ({ row }) => <KindCell row={row} deps={deps} />,
    },
    {
      field: 'grain',
      headerName: 'Tours',
      width: 190,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => valueLabel(deps.specs.get(KEY.grain), settingOf(row, KEY.grain)?.value),
      renderCell: ({ row }) => <GrainCell row={row} deps={deps} />,
    },
    ...(deps.specs.has(KEY.join)
      ? [
          {
            field: 'joins',
            headerName: 'Joins',
            width: 170,
            sortable: false,
            filterable: false,
            valueGetter: (_v: unknown, row: CatalogueRow) => {
              const target = joinTargetOf(row);
              if (target) return `joins ${target}`;
              const n = row.joined_by.length;
              return [
                row.pending_split_off ? `splits off ${splitOffFromLabel(row.pending_split_off)}` : '',
                n ? `+${n} joined` : '',
              ]
                .filter(Boolean)
                .join(' · ');
            },
            renderCell: ({ row }: { row: CatalogueRow }) => <JoinsCell row={row} deps={deps} />,
          } satisfies GridColDef<CatalogueRow>,
        ]
      : []),
    {
      field: 'languages',
      headerName: 'Languages',
      width: 110,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => optionLanguages(row).join(', '),
      renderCell: ({ row }) => <Typography variant="body2">{compactList(optionLanguages(row), 3)}</Typography>,
    },
    {
      field: 'start_times',
      headerName: 'Start times',
      width: 140,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => optionStartTimes(row).join(', '),
      renderCell: ({ row }) => {
        const times = optionStartTimes(row);
        return (
          <Tooltip describeChild title={times.join(', ')}>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {compactList(times, 3)}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'package',
      headerName: 'Package',
      width: 170,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => (row.is_package ? String(settingOf(row, KEY.packageMode)?.value ?? 'package') : ''),
      renderCell: ({ row }) => <PackageCell row={row} deps={deps} />,
    },
    {
      field: 'last_synced_ts',
      headerName: 'Last sync',
      width: 150,
      sortable: true,
      filterable: false,
      valueGetter: (_v, row) => row.last_synced_ts ?? '',
      renderCell: ({ row }) =>
        row.last_sync_status || row.last_synced_ts ? (
          <Stack spacing={0}>
            <Box>
              {row.last_sync_bgtask ? (
                <Chip
                  size="small"
                  label={row.last_sync_status ?? 'synced'}
                  color={syncStatusTone(row.last_sync_status)}
                  variant="outlined"
                  component={RouterLink}
                  to={`/bgtasks/${row.last_sync_bgtask}`}
                  clickable
                  sx={{ height: 20, fontSize: 11 }}
                />
              ) : (
                <SmallChip label={row.last_sync_status ?? 'synced'} color={syncStatusTone(row.last_sync_status)} variant="outlined" />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" title={formatDate(row.last_synced_ts)}>
              {formatRelativeTime(row.last_synced_ts)}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            never
          </Typography>
        ),
    },
    {
      field: 'ai',
      headerName: 'Itinerary / AI',
      width: 200,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) =>
        `${valueLabel(itinerarySpec, settingOf(row, KEY.itineraryMode)?.value)}${row.ai ? ` · AI ${aiRunLabel(row.ai)}` : ''}`,
      renderCell: ({ row }) => {
        const mode = settingOf(row, KEY.itineraryMode);
        const aiBuilt = row.outputs.filter((o) => o.ai_built && o.entity !== 'alias').length;
        return (
          <Stack spacing={0.25} sx={{ py: 0.5 }}>
            <Tooltip describeChild title={`${valueLabel(itinerarySpec, mode?.value)} · ${sourceLabel(mode?.source)}`}>
              <Typography variant="body2" noWrap>
                {mode ? String(mode.value) : '—'}
              </Typography>
            </Tooltip>
            {aiBuilt > 0 && (
              <Typography variant="caption" color="text.secondary">
                {aiBuilt} AI-built output{aiBuilt === 1 ? '' : 's'}
              </Typography>
            )}
            <AiStatusChips ai={row.ai} />
          </Stack>
        );
      },
    },
    {
      field: 'coupling',
      headerName: 'Coupling',
      width: 210,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) =>
        producedOutputs(row.outputs)
          .map((o) => `${o.entity}:${o.coupled ? 'coupled' : 'decoupled'}`)
          .join(' '),
      renderCell: ({ row }) => <CouplingCell row={row} deps={deps} />,
    },
    {
      field: 'pending',
      headerName: 'Upstream',
      width: 140,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => row.pending_change?.status ?? '',
      renderCell: ({ row }) => {
        const p = row.pending_change;
        if (!p) return null;
        return (
          <Tooltip
            describeChild
            title={`${p.summary || (p.removed ? 'removed from the listing' : 'changed upstream')}${p.apply?.state === 'blocked' ? ` · apply blocked (${p.apply.code ?? 'unknown'})` : ''}`}
          >
            <SmallChip
              label={p.status === 'pending' ? (p.review ? `review (${p.review})` : 'pending') : p.status}
              color={pendingTone(p.status)}
              variant={p.status === 'pending' ? 'filled' : 'outlined'}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 92,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderCell: ({ row }) => (
        <Button size="small" onClick={() => deps.onOpen(row)} aria-label={`Settings of ${row.title || row.name}`}>
          Settings
        </Button>
      ),
    },
  ];
}
